/**
 * GoalContentAddEditPageV2 — V2 edit-page chrome for per-goal onboarding content.
 *
 * Create and edit modes. Mirrors ExperimentAddEditPageV2: sticky
 * header + a Save button that fires the form via the native
 * form="goal-content-form" + type="submit" pairing.
 *
 * In edit mode the topic's Recommended Sessions list is embedded below the
 * form (the same editor as /goal-content/:slug/sessions). It saves on its own
 * button, separately from the topic form.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import AddEditGoalContentV2 from '../../../AddEditGoalContent/v2/AddEditGoalContentV2';
import EngagementSessionListPageV2 from '../../EngagementSessionListsPage/v2/EngagementSessionListPageV2';
import {
  useTopicSessionListProps,
  TOPIC_SESSION_LIST_DESCRIPTION,
} from '../../TopicSessionListPage/TopicSessionListPage';
import SessionHelper from '../../../Helper/SessionHelper';
import { goalLabel } from '../../../../constants/goalLabels';
import { Button } from '../../../redesign';

// Same permission as the /goal-content/:slug/sessions route (RouteConfig).
const PERMISSION_EDIT_SESSIONS = 'edit_sessions';
// `general` is the onboarding fallback — never a pickable topic, so it has no session list.
const FALLBACK_SLUG = 'general';

/** The topic's Recommended Sessions editor, embedded as a section of the edit page. */
function TopicRecommendedSessionsSection({ slug, onDirtyChange }) {
  const listProps = useTopicSessionListProps(slug);
  return (
    <section
      aria-label="Recommended Sessions"
      className="border-t border-sand"
      style={{ marginTop: 40, paddingTop: 28, maxWidth: 1200 }}
    >
      <EngagementSessionListPageV2
        key={slug}
        embedded
        onDirtyChange={onDirtyChange}
        title="Recommended Sessions"
        description={`${TOPIC_SESSION_LIST_DESCRIPTION} This list saves on its own — the topic's Save button does not save it.`}
        {...listProps}
      />
    </section>
  );
}

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function GoalContentAddEditPageV2({ mode = 'edit' }) {
  const params = useParams();
  const isCreate = mode === 'create';
  // The API label wins; the legacy constant is only a fallback for a row not loaded yet.
  const apiLabel = useSelector((s) =>
    (s.goalContent?.goalContents || []).find((c) => c.goal_slug === params.slug)?.label,
  );
  const title = isCreate ? 'New topic' : apiLabel || goalLabel(params.slug);
  // The list needs an existing topic (a known slug), and the edit_sessions permission the API asks for.
  const showRecommendedSessions =
    !isCreate &&
    !!params.slug &&
    params.slug !== FALLBACK_SLUG &&
    SessionHelper.hasPermission([PERMISSION_EDIT_SESSIONS]);

  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ updated_at }) => {
    setLastSavedAt(updated_at || null);
  }, []);

  // The topic Save navigates back to the list, which would silently drop unsaved
  // Recommended Sessions edits. Ask first; "Go back" cancels the topic save and stays here.
  const listDirtyRef = useRef(false);
  const handleListDirtyChange = useCallback((dirty) => {
    listDirtyRef.current = dirty;
  }, []);
  const confirmSubmit = useCallback(async () => {
    if (!listDirtyRef.current) return true;
    const result = await Swal.fire({
      icon: 'warning',
      title: 'You have unsaved Recommended Sessions changes',
      text: 'Saving the topic leaves this page, and those list changes will be lost. To keep them, go back and click Save changes in the Recommended Sessions section first.',
      showCancelButton: true,
      confirmButtonText: 'Discard list changes and save topic',
      cancelButtonText: 'Go back',
    });
    return !!result?.isConfirmed;
  }, []);

  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      })}`
    : null;

  return (
    <div className="bg-bg min-h-full">
      <div className="sticky top-0 bg-bg border-b border-sand" style={{ zIndex: 20 }}>
        <div className="flex items-start" style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}>
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {isCreate ? 'Creating topic' : 'Editing topic'}
            </div>
            <h1
              className="text-navy truncate"
              style={{ fontSize: 28, fontWeight: 700, margin: '4px 0 4px', letterSpacing: '-0.015em', lineHeight: 1.15 }}
              title={title}
            >
              {title}
            </h1>
            {!isCreate && (
              <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
                slug · <strong className="text-navy">{params.slug}</strong>
                {lastSavedLabel && <>{' · '}<span>{lastSavedLabel}</span></>}
              </div>
            )}
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/goal-content" className="no-underline">
              <Button variant="ghost">{isCreate ? 'Cancel' : 'Discard Changes'}</Button>
            </Link>
            <Button variant="coral" type="submit" form="goal-content-form" leadingIcon={SaveIconSvg}>
              {isCreate ? 'Create topic' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditGoalContentV2
          mode={mode}
          selectedSlug={params.slug}
          onLoaded={handleFormLoaded}
          confirmSubmit={showRecommendedSessions ? confirmSubmit : undefined}
        />
        {showRecommendedSessions && (
          <TopicRecommendedSessionsSection slug={params.slug} onDirtyChange={handleListDirtyChange} />
        )}
      </div>
    </div>
  );
}
